/**
 * fetch() with one retry for the failures a moment fixes, kept apart from
 * lib/api.ts so it can be tested (tests/unit/fetch-retry.test.mjs).
 *
 * Retried once, after RETRY_DELAY_MS, for a GET or HEAD only:
 *   - a network-layer failure (fetch threw a TypeError): iOS Safari
 *     backgrounding the tab, a connection reset, and a 502 from Cloudflare
 *     for an API that is restarting, which carries no CORS header and so
 *     reaches the page as a failed fetch (status 0 in the session replay,
 *     2026-11-01 21:38 UTC, during a backend deploy);
 *   - a 502 / 503 / 504 (and Cloudflare's 520-524) that does reach the page.
 * Never retried: an abort (the caller moved on), a POST or any other
 * method (a network error does not prove the request never reached the
 * server: /analyze, /compare, /sell would run twice). A network failure
 * that outlasts the retry, or any on a POST, becomes a NetworkError the
 * pages show as "check your connection".
 */

export class NetworkError extends Error {
  /** The original underlying fetch TypeError message, for debugging. */
  underlying: string;
  constructor(underlying: string) {
    super("Network request failed");
    this.name = "NetworkError";
    this.underlying = underlying;
  }
}

/** Detect the browser-specific "fetch couldn't complete" TypeError:
 *  WebKit  → "Load failed"
 *  Chrome  → "Failed to fetch"
 *  Firefox → "NetworkError when attempting to fetch resource." */
function isFetchNetworkError(err: unknown): boolean {
  return err instanceof TypeError && /load failed|failed to fetch|networkerror/i.test(err.message);
}

// The API restarting or too busy: Caddy's / cloudflared's 502, our 503
// (seo_busy, maintenance), a 504, Cloudflare's 520-524.
const TRANSIENT = new Set([502, 503, 504, 520, 521, 522, 523, 524]);
const RETRY_DELAY_MS = 1200;

function abortError(): DOMException {
  return new DOMException("The operation was aborted.", "AbortError");
}

/** Wait, unless the caller aborts first (then reject with an AbortError). */
function pause(ms: number, signal?: AbortSignal | null): Promise<void> {
  if (signal?.aborted) return Promise.reject(abortError());
  return new Promise((resolve, reject) => {
    const onAbort = () => {
      clearTimeout(timer);
      reject(abortError());
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

export async function fetchWithRetry(input: string, init?: RequestInit): Promise<Response> {
  const method = (init?.method ?? "GET").toUpperCase();
  const idempotent = method === "GET" || method === "HEAD";
  let first: Response;
  try {
    first = await fetch(input, init);
  } catch (err) {
    if (!isFetchNetworkError(err)) throw err;
    if (!idempotent || init?.signal?.aborted) throw new NetworkError((err as Error).message);
    return retry(input, init);
  }
  if (!idempotent || !TRANSIENT.has(first.status)) return first;
  first.body?.cancel().catch(() => undefined);
  return retry(input, init);
}

async function retry(input: string, init?: RequestInit): Promise<Response> {
  await pause(RETRY_DELAY_MS, init?.signal);
  try {
    return await fetch(input, init);
  } catch (err) {
    if (isFetchNetworkError(err)) throw new NetworkError((err as Error).message);
    throw err;
  }
}
